import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Collapse,
  Col,
  Form,
  Input,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import { BookOutlined, CopyOutlined, DownloadOutlined, KeyOutlined, PlusOutlined, QuestionCircleOutlined, ReloadOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';

import { ENDPOINTS } from '../../helpers/api/apiConfig';

const { Paragraph, Text } = Typography;

interface ApiTokenRow {
  id: number;
  name: string;
  tokenPrefix: string;
  maskedToken: string;
  scopes: string[];
  purpose: 'device_control' | 'external_read' | 'external_mission' | 'external_structure';
  deviceId: number | null;
  status: 'active' | 'revoked' | 'expired';
  createdAt: string;
  lastUsedAt: string | null;
  revokedAt: string | null;
  expiresAt: string | null;
  token?: string; // present only on create/rotate response
}

interface DeviceOption {
  id: number;
  label: string;
}

interface ApiTokensTabProps {
  buildAuthHeaders: (includeContentType?: boolean) => Record<string, string>;
}

const CURL_EXAMPLE = (token: string) => `curl -X POST http://<lightbox-ip>:5000/api/remote/v1/measurements/trigger \\
  -H "Authorization: Bearer ${token}" \\
  -H "Content-Type: application/json" \\
  -d '{"subAreaId": 1, "externalRef": "job-001", "metadata": {"operator": "alan"}}'`;

const EXTERNAL_CURL_EXAMPLE = (token: string) => `# List accessible devices
curl "${ENDPOINTS.EXTERNAL_DEVICES}" \\
  -H "Authorization: Bearer ${token}"

# Retrieve one device's measurements (follow pagination.nextCursor when present)
curl "${ENDPOINTS.EXTERNAL_DEVICE_MEASUREMENTS('<device-id>')}?from=2026-01-01T00%3A00%3A00Z&to=2026-01-31T23%3A59%3A59Z&limit=100" \\
  -H "Authorization: Bearer ${token}"`;

const EXTERNAL_MISSION_CURL_EXAMPLE = (token: string) => `curl -X POST "${ENDPOINTS.EXTERNAL_MISSION_DRAFTS}" \\
  -H "Authorization: Bearer ${token}" \\
  -H "Content-Type: application/json" \\
  -H "Idempotency-Key: job-4821" \\
  -d '{
    "externalReference": "job-4821",
    "name": "Inspection job 4821",
    "waypoints": [
      {"latitude": 51.501, "longitude": -0.142},
      {"latitude": 51.502, "longitude": -0.143}
    ]
  }'`;

const purposeLabel = (purpose: ApiTokenRow['purpose']) => {
  if (purpose === 'external_structure') return 'Areas and patterns';
  if (purpose === 'external_read') return 'External data';
  if (purpose === 'external_mission') return 'Mission drafts';
  return 'Device control';
};

const purposeColor = (purpose: ApiTokenRow['purpose']) => {
  if (purpose === 'external_read') return 'blue';
  if (purpose === 'external_mission') return 'orange';
  return 'purple';
};

const purposeExample = (purpose: ApiTokenRow['purpose'], token: string) => {
  if (purpose === 'external_structure') return `curl -X POST "${ENDPOINTS.EXTERNAL_AREAS}" -H "Authorization: Bearer ${token}" -H "Content-Type: application/json" -d '{"name":"Inspection area"}'`;
  if (purpose === 'external_read') return EXTERNAL_CURL_EXAMPLE(token);
  if (purpose === 'external_mission') return EXTERNAL_MISSION_CURL_EXAMPLE(token);
  return CURL_EXAMPLE(token);
};

const codeBlockStyle: React.CSSProperties = {
  background: '#f5f5f5',
  padding: 12,
  borderRadius: 4,
  overflowX: 'auto',
  marginBottom: 4,
};


const ApiTokensTab: React.FC<ApiTokensTabProps> = ({ buildAuthHeaders }) => {
  const navigate = useNavigate();
  const [tokens, setTokens] = useState<ApiTokenRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newTokenName, setNewTokenName] = useState('');
  const [newTokenPurpose, setNewTokenPurpose] = useState<ApiTokenRow['purpose'] | null>(null);
  const [newTokenDeviceId, setNewTokenDeviceId] = useState<number | undefined>();
  const [newTokenExpiresAt, setNewTokenExpiresAt] = useState('');
  const [devices, setDevices] = useState<DeviceOption[]>([]);
  const [rawTokenModal, setRawTokenModal] = useState<ApiTokenRow | null>(null);

  const loadTokens = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(ENDPOINTS.USER_API_TOKENS, {
        headers: { ...buildAuthHeaders() },
        credentials: 'include',
      });
      if (!res.ok) throw new Error(`Failed to load tokens (${res.status})`);
      const body = await res.json();
      setTokens(body.data || []);
    } catch (err: any) {
      message.error(err?.message || 'Failed to load API tokens');
    } finally {
      setLoading(false);
    }
  }, [buildAuthHeaders]);

  useEffect(() => {
    loadTokens();
  }, [loadTokens]);

  const loadDevices = useCallback(async () => {
    try {
      const res = await fetch(ENDPOINTS.GET_DEVICES, {
        headers: { ...buildAuthHeaders() },
        credentials: 'include',
      });
      if (!res.ok) return;
      const body = await res.json();
      setDevices((body.data || [])
        .filter((device: any) => Boolean(device.companyId))
        .map((device: any) => ({ id: device.id, label: device.label })));
    } catch {
      setDevices([]);
    }
  }, [buildAuthHeaders]);

  const openCreateModal = (purpose: ApiTokenRow['purpose']) => {
    setNewTokenPurpose(purpose);
    setNewTokenName('');
    setNewTokenDeviceId(undefined);
    setNewTokenExpiresAt('');
    loadDevices();
  };

  const createToken = async () => {
    if (newTokenPurpose === 'external_mission' && !newTokenDeviceId) {
      message.error('Select the robot that will receive missions from this token');
      return;
    }
    setCreating(true);
    try {
      const res = await fetch(ENDPOINTS.USER_API_TOKENS, {
        method: 'POST',
        headers: { ...buildAuthHeaders(), 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: newTokenName || (newTokenPurpose === 'external_structure' ? 'Area and pattern integration' : newTokenPurpose === 'external_read'
            ? 'External integration'
            : newTokenPurpose === 'external_mission'
              ? 'Mission integration'
              : 'Device control'),
          purpose: newTokenPurpose,
          deviceId: newTokenDeviceId,
          expiresAt: newTokenExpiresAt ? new Date(newTokenExpiresAt).toISOString() : undefined,
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.message || `Failed to create token (${res.status})`);
      }
      const body = await res.json();
      setNewTokenName('');
      setNewTokenPurpose(null);
      setNewTokenDeviceId(undefined);
      setNewTokenExpiresAt('');
      setRawTokenModal(body.data);
      await loadTokens();
    } catch (err: any) {
      message.error(err?.message || 'Failed to create API token');
    } finally {
      setCreating(false);
    }
  };

  const revokeToken = async (tokenId: number) => {
    try {
      const res = await fetch(ENDPOINTS.USER_API_TOKEN_REVOKE(tokenId), {
        method: 'POST',
        headers: { ...buildAuthHeaders() },
        credentials: 'include',
      });
      if (!res.ok) throw new Error(`Failed to revoke token (${res.status})`);
      message.success('Token revoked');
      await loadTokens();
    } catch (err: any) {
      message.error(err?.message || 'Failed to revoke token');
    }
  };

  const rotateToken = async (tokenId: number) => {
    try {
      const res = await fetch(ENDPOINTS.USER_API_TOKEN_ROTATE(tokenId), {
        method: 'POST',
        headers: { ...buildAuthHeaders(), 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({}),
      });
      if (!res.ok) throw new Error(`Failed to rotate token (${res.status})`);
      const body = await res.json();
      setRawTokenModal(body.data);
      await loadTokens();
    } catch (err: any) {
      message.error(err?.message || 'Failed to rotate token');
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard?.writeText(text).then(
      () => message.success('Copied to clipboard'),
      () => message.error('Copy failed — select and copy manually'),
    );
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Token',
      dataIndex: 'maskedToken',
      key: 'maskedToken',
      render: (value: string) => <Text code>{value}</Text>,
    },
    {
      title: 'Purpose',
      dataIndex: 'purpose',
      key: 'purpose',
      render: (purpose: ApiTokenRow['purpose']) => (
        <Tag color={purposeColor(purpose)}>
          {purposeLabel(purpose)}
        </Tag>
      ),
    },
    {
      title: 'Access',
      dataIndex: 'deviceId',
      key: 'deviceId',
      render: (deviceId: number | null) => deviceId ? `Device ${deviceId}` : 'All company devices',
    },
    {
      title: 'Permissions',
      dataIndex: 'purpose',
      key: 'permissions',
      render: (purpose: ApiTokenRow['purpose']) => purpose === 'external_structure'
        ? 'Create + archive areas and patterns; retrieve measurements'
        : purpose === 'external_read'
        ? 'List + retrieve'
        : purpose === 'external_mission'
          ? 'List robots + create drafts'
          : 'Trigger + upload',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={status === 'active' ? 'green' : status === 'revoked' ? 'red' : 'orange'}>
          {status}
        </Tag>
      ),
    },
    {
      title: 'Expires',
      dataIndex: 'expiresAt',
      key: 'expiresAt',
      render: (value: string | null) => (value ? new Date(value).toLocaleString() : 'Never'),
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (value: string) => (value ? new Date(value).toLocaleString() : '-'),
    },
    {
      title: 'Last used',
      dataIndex: 'lastUsedAt',
      key: 'lastUsedAt',
      render: (value: string | null) => (value ? new Date(value).toLocaleString() : 'Never'),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: unknown, record: ApiTokenRow) =>
        record.status === 'active' ? (
          <Space>
            <Popconfirm
              title="Rotate this token? The old value stops working."
              onConfirm={() => rotateToken(record.id)}
            >
              <Button size="small" icon={<ReloadOutlined />}>Rotate</Button>
            </Popconfirm>
            <Popconfirm
              title="Revoke this token? Devices using it will lose access."
              onConfirm={() => revokeToken(record.id)}
            >
              <Button size="small" danger>Revoke</Button>
            </Popconfirm>
          </Space>
        ) : null,
    },
  ];

  return (
    <div>
      <Card
        className="api-tokens-overview-card"
        title={<span><KeyOutlined /> API Tokens</span>}
        extra={(
          <Button className="api-tokens-wiki-button" type="primary" icon={<BookOutlined />} onClick={() => navigate('/docs/integrations')}>
            Open Integration Wiki
          </Button>
        )}
        style={{ marginBottom: 16 }}
      >
        <Paragraph type="secondary">
          Create separate credentials for controlling Lightboxes, read-only data access, and
          external mission submission, and area and pattern management. Permissions are fixed by token purpose and cannot be mixed.
        </Paragraph>
        <Row gutter={[16, 16]}>
          <Col xs={24} lg={8}>
            <Card size="small" title="Area and Pattern Management">
              <Paragraph type="secondary">
                Create areas, subareas, and patterns; archive or restore them; retrieve measurements
                with their area, pattern, and point IDs. Writes require a company administrator.
              </Paragraph>
              <Button type="primary" icon={<PlusOutlined />} onClick={() => openCreateModal('external_structure')}>
                Create management token
              </Button>
            </Card>
          </Col>
          <Col xs={24} lg={8}>
            <Card size="small" title="Lightbox / Device Control">
              <Paragraph type="secondary">
                Trigger measurements and support the authenticated Lightbox upload workflow.
                This token cannot use the external retrieval API.
              </Paragraph>
              <Button type="primary" icon={<PlusOutlined />} onClick={() => openCreateModal('device_control')}>
                Create control token
              </Button>
            </Card>
          </Col>
          <Col xs={24} lg={8}>
            <Card size="small" title="External Measurement Access">
              <Paragraph type="secondary">
                List accessible devices and retrieve measurements by device, area, or pattern point. This token
                is read-only and cannot control devices or upload data.
              </Paragraph>
              <Button type="primary" icon={<PlusOutlined />} onClick={() => openCreateModal('external_read')}>
                Create retrieval token
              </Button>
            </Card>
          </Col>
          <Col xs={24} lg={8}>
            <Card size="small" title="External Mission Drafts">
              <Paragraph type="secondary">
                Link a token to one robot, then submit GPS waypoints as an area-less draft. An
                OSCAR operator must select the area and add START and END before execution.
              </Paragraph>
              <Button type="primary" icon={<PlusOutlined />} onClick={() => openCreateModal('external_mission')}>
                Create mission token
              </Button>
            </Card>
          </Col>
        </Row>
      </Card>

      <Modal
        open={!!newTokenPurpose}
        title={newTokenPurpose === 'external_structure' ? 'Create area and pattern management token' : newTokenPurpose === 'external_read'
          ? 'Create external retrieval token'
          : newTokenPurpose === 'external_mission'
            ? 'Create mission integration token'
            : 'Create device-control token'}
        okText="Create token"
        confirmLoading={creating}
        onOk={createToken}
        onCancel={() => setNewTokenPurpose(null)}
      >
        <Alert
          type={newTokenPurpose === 'device_control' ? 'warning' : 'info'}
          showIcon
          style={{ marginBottom: 16 }}
          message={newTokenPurpose === 'external_structure'
            ? 'Company-wide area and pattern access, plus measurement retrieval. Device restrictions apply only to measurement retrieval.'
            : newTokenPurpose === 'external_read'
              ? 'Read-only: list devices and retrieve measurements in a date range.'
            : newTokenPurpose === 'external_mission'
              ? 'Draft-only: submit area-less waypoint mission drafts for the selected robot.'
              : 'Control access: can trigger measurements and participate in uploads.'}
        />
        <Form layout="vertical">
          <Form.Item label="Token name" required>
            <Input
              autoFocus
              placeholder={newTokenPurpose === 'external_read'
                ? 'e.g. Partner A Production'
                : newTokenPurpose === 'external_mission'
                  ? 'e.g. Mission Provider Production'
                  : 'e.g. Packhouse PLC'}
              value={newTokenName}
              onChange={(e) => setNewTokenName(e.target.value)}
            />
          </Form.Item>
          <Form.Item
            label={newTokenPurpose === 'external_mission' ? 'Mission robot' : 'Device restriction'}
            required={newTokenPurpose === 'external_mission'}
            extra={newTokenPurpose === 'external_mission'
              ? 'Every mission submitted with this token is assigned to this robot automatically.'
              : 'Leave empty to allow all devices assigned to the current company.'}
          >
            <Select
              allowClear={newTokenPurpose !== 'external_mission'}
              showSearch
              optionFilterProp="label"
              placeholder={newTokenPurpose === 'external_mission' ? 'Select a robot' : 'All company devices'}
              value={newTokenDeviceId}
              onChange={(value) => setNewTokenDeviceId(value)}
              options={devices.map((device) => ({ value: device.id, label: `${device.label} (#${device.id})` }))}
            />
          </Form.Item>
          <Form.Item label="Expiration" extra="Optional. Expired tokens stop working automatically.">
            <Input
              type="datetime-local"
              value={newTokenExpiresAt}
              onChange={(e) => setNewTokenExpiresAt(e.target.value)}
            />
          </Form.Item>
        </Form>
      </Modal>

      <Table
        rowKey="id"
        dataSource={tokens}
        columns={columns}
        loading={loading}
        pagination={{ pageSize: 10, hideOnSinglePage: true }}
      />

      <Collapse
        style={{ marginTop: 16 }}
        items={[
          {
            key: 'device-control',
            label: (
              <span><QuestionCircleOutlined /> Lightbox / Device Control quick start</span>
            ),
            children: (
              <div>
                <Alert
                  type="info"
                  showIcon
                  style={{ marginBottom: 16 }}
                  message="Use a Device Control token with the Lightbox local API. First use must happen while the box can reach OSCAR; later Ethernet use can work offline."
                />
                <Space wrap style={{ marginBottom: 16 }}>
                  <Button
                    type="primary"
                    icon={<BookOutlined />}
                    onClick={() => navigate('/docs/integrations#device-control')}
                  >
                    Open full control guide
                  </Button>
                  <Button
                    icon={<DownloadOutlined />}
                    href="/docs/examples/trigger_measurement.py"
                    download="trigger_measurement.py"
                  >
                    Download Python example
                  </Button>
                </Space>
                <Paragraph type="secondary">
                  The wiki explains discovery, readiness, safe retries, upload synchronization and
                  touchscreen handover. For a known Lightbox address, the basic trigger request is:
                </Paragraph>
                <Paragraph>
                  <pre style={codeBlockStyle}>{CURL_EXAMPLE('<your-token>')}</pre>
                  <Button
                    type="link"
                    size="small"
                    icon={<CopyOutlined />}
                    onClick={() => copyToClipboard(CURL_EXAMPLE('<your-token>'))}
                  >
                    Copy example
                  </Button>
                </Paragraph>
                <Alert
                  type="warning"
                  showIcon
                  message="After a remote trigger, the API-token user remains the active owner until an explicit screen handover or touchscreen login."
                />
              </div>
            ),
          },
          {
            key: 'external-read',
            label: (
              <span><QuestionCircleOutlined /> External Measurement Access quick start</span>
            ),
            children: (
              <div>
                <Alert
                  type="info"
                  showIcon
                  style={{ marginBottom: 16 }}
                  message="Use an External Measurement Access token. It is read-only and works with the versioned external retrieval endpoints."
                />
                <Button
                  type="primary"
                  icon={<BookOutlined />}
                  style={{ marginBottom: 16 }}
                  onClick={() => navigate('/docs/integrations#measurement-retrieval')}
                >
                  Open full retrieval guide
                </Button>
                <Paragraph type="secondary">
                  First list the devices available to the token, then request measurements for one
                  returned device ID. The wiki documents date ranges, pagination, linked values,
                  texts, ready files and temporary download URLs.
                </Paragraph>
                <Paragraph>
                  <pre style={codeBlockStyle}>{EXTERNAL_CURL_EXAMPLE('<your-token>')}</pre>
                  <Button
                    type="link"
                    size="small"
                    icon={<CopyOutlined />}
                    onClick={() => copyToClipboard(EXTERNAL_CURL_EXAMPLE('<your-token>'))}
                  >
                    Copy examples
                  </Button>
                </Paragraph>
              </div>
            ),
          },
        ]}
      />

      <Modal

        open={!!rawTokenModal}
        title="API token created — copy it now"
        onCancel={() => setRawTokenModal(null)}
        footer={[
          <Button key="close" type="primary" onClick={() => setRawTokenModal(null)}>
            I have copied the token
          </Button>,
        ]}
        width={720}
      >
        {rawTokenModal?.token && (
          <>
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
              message="This token is shown only once. Store it securely — it cannot be retrieved later."
            />
            <Paragraph>
              <Text code style={{ fontSize: 14, wordBreak: 'break-all' }}>{rawTokenModal.token}</Text>
              <Button
                type="link"
                icon={<CopyOutlined />}
                onClick={() => copyToClipboard(rawTokenModal.token!)}
              >
                Copy
              </Button>
            </Paragraph>
            <Paragraph strong style={{ marginTop: 16 }}>
              {rawTokenModal.purpose === 'external_structure' ? 'Create an area in OSCAR:' : rawTokenModal.purpose === 'external_read'
                ? 'Use the external read-only API:'
                : rawTokenModal.purpose === 'external_mission'
                  ? 'Create an area-less mission draft:'
                  : 'Trigger a measurement on a Lightbox:'}
            </Paragraph>
            <Paragraph>
              <pre style={{ background: '#f5f5f5', padding: 12, borderRadius: 4, overflowX: 'auto' }}>
                {purposeExample(rawTokenModal.purpose, rawTokenModal.token)}
              </pre>
              <Button
                type="link"
                icon={<CopyOutlined />}
                onClick={() => copyToClipboard(
                  purposeExample(rawTokenModal.purpose, rawTokenModal.token!),
                )}
              >
                Copy example
              </Button>
            </Paragraph>
          </>
        )}
      </Modal>
    </div>
  );
};

export default ApiTokensTab;
